/**
 * loadGaussianScene: load a .sog / .lod-meta.json gaussian splat asset.
 *
 * The 180° Z rotation matches the splat coordinate system to PlayCanvas world
 * space (same convention as supersplat-viewer). `unified: true` selects the
 * unified gsplat renderer; the engine auto-detects streamed LOD when the URL
 * ends in lod-meta.json.
 *
 * NOTE: this resolves as soon as the asset is *parsed* (lod-meta.json + octree
 * ready) — NOT when the per-view chunks have streamed in. For a streamed scene
 * that means the visible splats keep refining after entry. Call
 * `preloadGaussianScene` (with the player spawn) to load the spawn-area fine
 * LOD + coarse coverage up front and drive a real load progress bar before
 * fading in.
 */

import { Asset, Entity, Vec3, WORKBUFFER_UPDATE_ALWAYS } from 'playcanvas';
import type { Application } from 'playcanvas';

export interface GaussianSceneHandle {
    entity: Entity;
    asset: Asset;
}

export interface GaussianLoadOptions {
    mobileLowQuality?: boolean;
    preloadStage?: 'coarse' | 'full' | 'near';
    /**
     * Preload a specific LOD index around the spawn instead of the
     * stage-dependent default. Used by the mobile background 'near' preload
     * to cap itself below the finest allowed LOD (bounded download/memory).
     */
    nearLod?: number;
    /** Meters around the spawn the 'near'/'full' preload covers. */
    nearRadius?: number;
}

// Finest LOD index mobile may select (0 = 100% density). 1 allows the 60%
// level; the 2 it replaced capped mobile at 40% density on top of the
// splatBudget balancer, which measurably pinned whole scenes at LOD4-6.
const MOBILE_SHARED_HIGH_LOD_INDEX = 1;
const MOBILE_LEGACY_FINEST_LOD_COUNT = 4;

// The engine's SOG chunk streaming loader (GSplatAssetLoader) defaults to
// maxConcurrentLoads = 2 — one global FIFO pipe shared by the engine's own
// on-demand LOD requests and every ensureFileResource preload call. Measured
// on mainhall desktop: 43 near-field LOD0 preload chunks sat in that queue
// behind the engine's L2/L3 desires and ZERO LOD0 chunks had even started
// downloading 25s after entry. Widening the pipe is what lets the preload
// actually win the race.
const DESKTOP_CHUNK_LOAD_CONCURRENCY = 12;
const MOBILE_CHUNK_LOAD_CONCURRENCY = 6;

function mobilePreferredLod(asset: Asset): number {
    const resource = asset.resource as { octree?: { lodLevels?: number } } | null;
    const levels = resource?.octree?.lodLevels;
    if (typeof levels !== 'number' || levels <= 0) return 0;
    if (levels >= 7) {
        return Math.min(MOBILE_SHARED_HIGH_LOD_INDEX, levels - 1);
    }
    return Math.max(0, levels - MOBILE_LEGACY_FINEST_LOD_COUNT);
}

export function loadGaussianScene(
    app: Application,
    url: string,
    onProgress?: (fraction: number) => void,
    options: GaussianLoadOptions = {}
): Promise<GaussianSceneHandle> {
    return new Promise((resolve, reject) => {
        const filename = url.split('/').pop() || 'scene';
        const asset = new Asset(filename, 'gsplat', { url });
        console.info(`[gsplat-load] start url=${url} mobile=${Boolean(options.mobileLowQuality)}`);

        // Streamed SOG loads many chunk textures; forward per-asset download
        // progress (the lod-meta fetch). The bulk of "real" loading happens in
        // preloadGaussianScene after the spawn is known.
        asset.on('progress', (received: number, length: number) => {
            const fraction = length > 0 ? Math.min(0.2, 0.05 + 0.15 * received / length) : 0.05;
            console.info(`[gsplat-load] progress url=${url} received=${received} length=${length} fraction=${fraction.toFixed(3)}`);
            if (length > 0) onProgress?.(fraction);
        });

        asset.on('load', () => {
            const entity = new Entity('GaussianScene');
            // Flip splat space into PlayCanvas world space.
            entity.setLocalEulerAngles(0, 0, 180);
            entity.addComponent('gsplat', {
                asset,
                unified: true
            });
            // The gsplat stays on its default World layer (LAYERID_WORLD); the
            // camera includes that layer. Reassigning gsplat.layers here would
            // run before the entity is parented, and GSplatComponent.set layers
            // skips addToLayers() while the entity is not enabled in hierarchy —
            // leaving the splat in no layer (black screen).
            app.root.addChild(entity);

            // Mobile caps the finest allowed LOD, but keeps the far end adaptive.
            // NOTE: lodBaseDistance / lodMultiplier are deliberately not set —
            // PlayCanvas 2.20 deprecated them to no-op shims (their setters are
            // empty and the getters return 0). LOD selection is now driven by the
            // global `scene.gsplat.splatBudget` balancer (see createApp), so
            // setting them here would silently do nothing.
            const mobileLod = mobilePreferredLod(asset);
            if (entity.gsplat) {
                entity.gsplat.lodRangeMin = options.mobileLowQuality ? mobileLod : 0;
                entity.gsplat.lodRangeMax = Number.POSITIVE_INFINITY;
            }
            if (entity.gsplat && !options.mobileLowQuality) {
                entity.gsplat.workBufferUpdate = WORKBUFFER_UPDATE_ALWAYS;
            }
            const resource = asset.resource as { octree?: { lodLevels?: number } } | null;
            console.info(`[gsplat-load] loaded url=${url} lodLevels=${String(resource?.octree?.lodLevels ?? 'unknown')} mobileLod=${mobileLod}`);

            // Widen the chunk-streaming pipe (see constant comment above).
            const chunkLoader = (asset.resource as { octree?: { assetLoader?: { maxConcurrentLoads?: number } } } | null)
                ?.octree?.assetLoader;
            if (chunkLoader && typeof chunkLoader.maxConcurrentLoads === 'number') {
                chunkLoader.maxConcurrentLoads = options.mobileLowQuality
                    ? MOBILE_CHUNK_LOAD_CONCURRENCY
                    : DESKTOP_CHUNK_LOAD_CONCURRENCY;
                console.info(`[gsplat-load] chunk loader maxConcurrentLoads=${chunkLoader.maxConcurrentLoads}`);
            }

            resolve({ entity, asset });
        });

        asset.on('error', (err: unknown) => {
            console.warn(`[gsplat-load] error url=${url}`, err);
            reject(new Error(typeof err === 'string' ? err : `Failed to load ${url}`));
        });

        app.assets.add(asset);
        app.assets.load(asset);
    });
}

/** Scratch vector for the preload distance check. */
const _worldCenter = new Vec3();

/**
 * Preload streamed-gsplat chunks around the player spawn BEFORE entry so the
 * first view is covered (no black flash on the first turn). Drives `onProgress`
 * 0→1 with the blocking set's chunk-load ratio; the coarse entry preload must
 * complete; full/near background preloads may time out.
 *
 * Strategy — spawn-core-fine-first, coarse-then-ring-fine (the chunk loader is
 * a FIFO queue, so ensure order is download priority order):
 *   - Pass 1  spawn-core fine (≤10m, desktop) — the player's exact standing
 *     spot refines to full detail FIRST and BLOCKS entry on 'coarse'/'full':
 *     the user spawns standing in high detail instead of coarse filler.
 *   - Pass 2  near coarse (within radius) — blocking; first-view coverage for
 *     the rest of the near field.
 *   - Pass 3  ring fine (10-30m, desktop) — enqueued right behind the blocking
 *     coarse set and AHEAD of all far passes, so the near field refines to
 *     high detail before anything far does. Desktop 'coarse' enqueues without
 *     waiting; 'full' waits in background.
 *   - Pass 4  far coarse (scene-wide) — ordered near→far, non-blocking; far
 *     coarse coverage streams in behind the near-field detail. Mobile never
 *     sweeps scene-wide and defers fine detail to its bounded 'near' stage
 *     (the old mobile 'full' preload OOM-killed meihuashan, 781MB/125 chunks).
 */
export async function preloadGaussianScene(
    handle: GaussianSceneHandle,
    spawn: Vec3,
    onProgress?: (fraction: number) => void,
    options: GaussianLoadOptions = {}
): Promise<void> {
    const stage = options.preloadStage ?? 'full';
    const res = handle.asset.resource as {
        octree?: {
            lodLevels: number;
            nodes: { boundingSphere: Vec3; lods: { fileIndex: number }[] }[];
            ensureFileResource: (fileIndex: number) => void;
            getFileResource: (fileIndex: number) => unknown;
        };
    } | null;
    const octree = res?.octree;
    if (!octree || !octree.nodes?.length) {
        onProgress?.(1);
        return;
    }

    const maxLod = octree.lodLevels - 1;
    const fallbackLod = maxLod;
    const nearLod = options.nearLod ?? (options.mobileLowQuality ? mobilePreferredLod(handle.asset) : 0);
    // Desktop 30m matches the 12M splat budget's measured LOD0 reach
    // (~26.6m on laomendong2), so the background fine preload covers every
    // chunk the balancer will actually pin at LOD0 around the spawn.
    const nearRadius = options.nearRadius ?? (options.mobileLowQuality ? 34 : 30); // meters around spawn to preload visible detail
    // Core ring around the exact spawn: its FINE LOD blocks entry on desktop
    // so the player appears standing in full detail. Kept small (10m) — a
    // handful of LOD0 chunks, seconds of extra loader time.
    const coreRadius = options.mobileLowQuality ? 0 : 10;
    const ensured = new Set<number>();

    // Distance-ordered passes (the chunk loader is a FIFO queue, so ensure
    // order is download priority order):
    //   Pass 1  spawn-core fine (≤10m, desktop) — blocks entry; highest priority
    //   Pass 2  near coarse (within radius)     — blocks entry; first-view coverage
    //   Pass 3  ring fine (10-30m, desktop)     — enqueued ahead of far passes
    //   Pass 4  far coarse (scene-wide)         — streaming coverage, near→far
    type NodeEntry = { node: { lods: { fileIndex: number }[] }; dist: number };
    const nodesByDistance: NodeEntry[] = octree.nodes
        .filter((node) => node.lods)
        .map((node) => ({ node, dist: nodeDistance(node, spawn) }))
        .sort((a, b) => a.dist - b.dist);
    const core = nodesByDistance.filter((e) => e.dist < coreRadius);
    const ring = nodesByDistance.filter((e) => e.dist >= coreRadius && e.dist < nearRadius);
    const near = nodesByDistance.filter((e) => e.dist < nearRadius);
    const far = nodesByDistance.filter((e) => e.dist >= nearRadius);
    const ensurePass = (entries: NodeEntry[], lod: number, required?: Set<number>) => {
        for (const { node } of entries) {
            const fi = node.lods[Math.min(lod, maxLod)]?.fileIndex;
            if (fi == null || fi === -1) continue;
            if (!ensured.has(fi)) {
                octree.ensureFileResource(fi);
                ensured.add(fi);
            }
            required?.add(fi);
        }
    };

    // Pass 1 — spawn-core FINE first (desktop): the exact standing spot gets
    // download priority AND blocks entry, so the player appears standing in
    // full detail instead of coarse filler.
    const required = new Set<number>();
    if (!options.mobileLowQuality) {
        ensurePass(core, nearLod, required);
    }
    // Pass 2 — near coarse: first-view coverage for the rest of the near
    // field (blocking; closest chunks download first).
    ensurePass(near, fallbackLod, required);
    // Pass 3 — ring fine right behind the blocking coarse set (desktop
    // coarse/full only; mobile defers fine detail to its bounded 'near' stage
    // to avoid the OOM sweep). Enqueued non-blocking: it races the far passes
    // for bandwidth but sits ahead of them in the FIFO queue.
    if (options.mobileLowQuality) {
        // mobile：精模延迟到独立的 'near' 阶段（有界下载/内存，防 OOM）。
        if (stage === 'near') ensurePass(near, nearLod, required);
    } else if (stage !== 'near') {
        // 桌面 coarse/full：核心圈外的精模紧随粗模入队。coarse 只入队不等待
        // （不拖慢进入）；full 后台等到全部近场精模完成。
        ensurePass(ring, nearLod, stage === 'full' ? required : undefined);
    }
    // Pass 4 — far coarse: scene-wide coarse coverage (ordered near→far).
    if (stage === 'coarse' || stage === 'full') ensurePass(far, fallbackLod);

    const fis = [...ensured];
    // Only the required set blocks entry; the rest keeps streaming in the
    // background after the scene fades in.
    const requiredFis = fis.filter((fi) => required.has(fi));
    const total = requiredFis.length;
    if (fis.length === 0 || total === 0) {
        onProgress?.(1);
        return;
    }
    const coverageLabel = stage === 'coarse'
        ? `spawn-core fine LOD ${nearLod} within ${coreRadius}m (blocking) + near coarse LOD ${fallbackLod} within ${nearRadius}m (far streams behind)`
        : stage === 'near'
            ? `near LOD ${nearLod} within ${nearRadius}m only`
            : `spawn-core fine LOD ${nearLod} within ${coreRadius}m + ring fine within ${nearRadius}m + scene-wide coarse LOD ${fallbackLod}, near-first`;
    console.log(`[gsplat] preloading ${fis.length} chunks (${stage}, ${coverageLabel}) around spawn ${spawn.x},${spawn.y},${spawn.z}; blocking on ${total}`);

    const POLL_MS = 50;
    const minRequiredLoaded = total;
    // The 'full' timeout was 15s; measured on mainhall that gave up at 2/45
    // chunks because the queue was still draining the engine's own mid-tier
    // requests. 60s lets a real network finish the near-field LOD0 set.
    // Desktop 'coarse' 12s → 20s: its blocking set now also includes the
    // spawn-core FINE LOD (a handful of large LOD0 chunks), not just coarse.
    const hardTimeoutMs = stage === 'coarse'
        ? (options.mobileLowQuality ? 30000 : 20000)
        : stage === 'near' ? 30000 : 60000;
    const now0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    let lastProgressLogMs = now0;
    let loaded = 0;
    let timedOut = false;
    while (true) {
        for (const fi of fis) if (!octree.getFileResource(fi)) octree.ensureFileResource(fi);
        loaded = requiredFis.filter((fi) => octree.getFileResource(fi)).length;
        onProgress?.(Math.max(0.2, Math.min(0.99, loaded / total)));
        const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
        if (loaded >= minRequiredLoaded) break;
        if (now - lastProgressLogMs > 1000) {
            lastProgressLogMs = now;
            console.info(`[gsplat] preload progress (${stage}): ${loaded}/${total} chunks resident, need ${minRequiredLoaded}`);
        }
        if (now - now0 > hardTimeoutMs) {
            timedOut = true;
            console.warn(`[gsplat] preload timeout (${stage}): gave up with ${loaded}/${total} chunks resident, need ${minRequiredLoaded}`);
            if (stage === 'coarse') {
                throw new Error(`GSplat coarse preload timed out before first visible LOD coverage: ${loaded}/${total} chunks resident`);
            }
            break;
        }
        await new Promise((r) => setTimeout(r, POLL_MS));
    }
    onProgress?.(1);
    if (timedOut) {
        console.warn(`[gsplat] preload incomplete (${stage}): stopped at ${loaded}/${total} chunks resident; engine streaming continues`);
    } else {
        console.log(`[gsplat] preload done (${stage}): ${loaded}/${total} chunks resident`);
    }
}

/**
 * Distance from a splat-space octree node to a world-space point. The gsplat
 * entity only has the 180° Z rotation (no translation/scale), so
 * world = Rz(180) * splat = (-x, -y, z). (Done manually because the entity's
 * world transform may not be synced yet at preload time.)
 */
function nodeDistance(node: { boundingSphere?: Vec3 }, spawn: Vec3): number {
    const bs = node.boundingSphere;
    if (bs) {
        _worldCenter.set(-bs.x, -bs.y, bs.z);
    } else {
        _worldCenter.set(0, 0, 0);
    }
    return _worldCenter.distance(spawn);
}
